import { useCallback, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useTheme } from "../theme/ThemeProvider";
import { CLUB_SHOTS, shotSrc } from "../data/shots";
import DragCarousel from "../ui/DragCarousel";
import DeviceIphone from "../ui/DeviceIphone";
import Screen from "../ui/Screen";
import "./showcase.css";

/*
 * A gym's day, in the order an owner lives it, with the screen that carries
 * each hour. The times are the argument: this section is not a tour of
 * features, it is the answer to "when would I actually open this?"
 *
 * Copy is the screen's own headline from shots.js where that already reads
 * well, rewritten here only where the store line leans on a dash.
 */
const DAY = [
  {
    slug: "today",
    time: "6:00",
    meridiem: "am",
    moment: "Before the shutter goes up",
    headline: "Every morning,\nalready sorted.",
    sub: "Who owes, who is expiring, and who is waiting, ready before you open the door.",
  },
  {
    slug: "checkin",
    time: "7:15",
    meridiem: "am",
    moment: "The morning batch walks in",
    headline: "Let the floor\ncheck itself in.",
    sub: "Members scan your QR and type their number, or you tap a name. Every check-in lands on that member's record.",
  },
  {
    slug: "members",
    time: "11:00",
    meridiem: "am",
    moment: "A quiet hour at the desk",
    headline: "Your whole roster,\none search away.",
    sub: "Active, owing, expiring, gone quiet. Select several to assign a workout or send one message.",
  },
  {
    slug: "money",
    time: "2:30",
    meridiem: "pm",
    moment: "Counting the month",
    headline: "Where the month\nactually went.",
    sub: "Collected against pending, by plan and by method. Export a date range.",
  },
  {
    slug: "staff",
    time: "5:00",
    meridiem: "pm",
    moment: "The evening trainer takes over",
    headline: "Give a trainer the floor,\nnot the books.",
    sub: "Trainers get members, workouts and check-in. Money stays yours.",
  },
  {
    slug: "reminders",
    time: "9:30",
    meridiem: "pm",
    moment: "Lights off, dues sent",
    headline: "Chase dues without\nchasing anyone.",
    sub: "Overdue and expiry reminders are drafted for you. Send them yourself or let them go out automatically.",
  },
].map((step) => ({ ...step, ...pick(step.slug) }));

function pick(slug) {
  const { screen, inFrame } = CLUB_SHOTS.find((s) => s.slug === slug);
  return { screen, inFrame };
}

/**
 * One phone, pinned, its screen advancing through a gym's day as the page
 * scrolls, with a clock keeping time beside it. Short on purpose: about three
 * screens of scroll for six hours, so it never feels like the page has stopped.
 *
 * Below 1068px this becomes a drag carousel instead. Pinning a tall section on
 * a phone means hijacking the one gesture the reader owns.
 */
export default function Showcase() {
  const reduce = useReducedMotion();

  return (
    <section className="section showcase" id="screens">
      <div className="container container--wide showcase__head">
        <h2 className="t-display-1 showcase__title">
          One day at your gym.
        </h2>
        <p className="t-lead showcase__intro">
          From opening the shutter to sending the last reminder, this is
          where the day goes.
        </p>
      </div>

      {!reduce && <ShowcasePinned />}
      <ShowcaseCarousel forceVisible={reduce} />
    </section>
  );
}

/* Pinned, scroll-driven. Desktop only: CSS hides it below 1068px. */
function ShowcasePinned() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const i = Math.floor(p * DAY.length);
    setActive(Math.min(DAY.length - 1, Math.max(0, i)));
  });

  // Hour labels sit centred in six equal columns: travel centre to centre.
  const markerX = useTransform(scrollYProgress, [0, 1], ["8.333%", "91.667%"]);

  /*
   * Clicking a time scrolls to the middle of that hour's slice rather than to
   * its start, so the click never lands a pixel short of the screen it named.
   */
  const goTo = useCallback((index) => {
    const node = ref.current;
    if (!node) return;
    const { top, height } = node.getBoundingClientRect();
    const start = window.scrollY + top;
    const scrollable = height - window.innerHeight;
    const target = start + (scrollable * (index + 0.5)) / DAY.length;
    window.scrollTo({ top: target, behavior: "smooth" });
  }, []);

  const step = DAY[active];

  return (
    <div className="showcase__pinned" ref={ref} data-cta-yield="true">
      <div className="showcase__sticky">
        <div className="container container--wide showcase__stage">
          <div className="showcase__copy">
            {/*
             * The words swap, they do not cross-fade. Two screenshots
             * overlapping at half opacity reads as depth; two sentences
             * overlapping reads as a rendering fault.
             */}
            <div className="showcase__clock" aria-hidden="true">
              <AnimatePresence initial={false} mode="popLayout">
                <motion.span
                  className="showcase__time"
                  key={step.time}
                  initial={{ y: "60%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: "-60%", opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Clock time={step.time} />
                  <span className="showcase__meridiem">{step.meridiem}</span>
                </motion.span>
              </AnimatePresence>
            </div>

            <div className="showcase__caption">
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  className="showcase__caption-item"
                  key={step.slug}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
                >
                  <p className="showcase__moment">{step.moment}</p>
                  <h3 className="showcase__caption-headline">
                    {step.headline.split("\n").map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                  </h3>
                  <p className="t-lead showcase__caption-sub">{step.sub}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="showcase__device">
            <DeviceIphone>
              <div className="showcase__bezel">
                {DAY.map((s, i) => (
                  <Layer key={s.slug} step={s} index={i} on={i === active} />
                ))}
              </div>
            </DeviceIphone>
          </div>
        </div>

        {/*
         * The day as a line. Every time is a button: a reader who wants the
         * evening should not have to scroll through the morning to get there.
         */}
        <div className="container container--wide showcase__timeline">
          <div className="showcase__track" aria-hidden="true">
            {/* A full-width carrier translated by its own width, so the
                marker moves on transform rather than on `left`. */}
            <motion.span className="showcase__carrier" style={{ x: markerX }}>
              <span className="showcase__marker" />
            </motion.span>
          </div>
          <ol className="showcase__hours">
            {DAY.map((s, i) => (
              <li key={s.slug}>
                <button
                  className="showcase__hour"
                  type="button"
                  aria-current={i === active}
                  onClick={() => goTo(i)}
                >
                  <span className="showcase__hour-time">
                    {s.time} {s.meridiem}
                  </span>
                  <span className="showcase__hour-screen">{s.screen}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

/* The colon is tightened on its own: at display size the font's colon sits in
   a full tabular slot and reads as "6 : 00". */
function Clock({ time }) {
  const [h, m] = time.split(":");
  return (
    <span className="showcase__digits">
      {h}
      <span className="showcase__colon">:</span>
      {m}
    </span>
  );
}

/**
 * One screen in the stack.
 *
 * Swapped on the hour, not cross-faded along the scroll. A scroll-linked fade
 * left two screens half-visible whenever the reader stopped between hours;
 * this one fades the new screen in over the old in a quarter of a second and
 * then drops the old one (showcase.css), so a still page always shows one.
 */
function Layer({ step, index, on }) {
  const { theme } = useTheme();

  return (
    <div className="showcase__layer" data-on={on || undefined}>
      <picture>
        <source srcSet={shotSrc(step.slug, theme, "webp")} type="image/webp" />
        <img
          key={theme}
          // Before src: see ShotImage in ui/Screen.jsx.
          loading="lazy"
          decoding="async"
          src={shotSrc(step.slug, theme, "png")}
          alt={`The ${step.screen} screen. ${step.inFrame}`}
          width="660"
          height="1434"
          draggable="false"
        />
      </picture>
    </div>
  );
}

/* Below 1068px, and the only version that renders under reduced motion. */
function ShowcaseCarousel({ forceVisible }) {
  return (
    <div
      className={`container showcase__carousel${
        forceVisible ? " showcase__carousel--always" : ""
      }`}
    >
      <DragCarousel
        items={DAY}
        getKey={(s) => s.slug}
        label="A day in Varzish Mate"
        renderItem={(s) => (
          <article className="showcase__card">
            <p className="showcase__card-time">
              <Clock time={s.time} />
              <span className="showcase__meridiem">{s.meridiem}</span>
            </p>
            <Screen
              slug={s.slug}
              alt={`The ${s.screen} screen. ${s.inFrame}`}
              className="showcase__card-screen"
              buttons={false}
            />
            <div className="showcase__card-body">
              <h3 className="showcase__card-headline">
                {s.headline.replace("\n", " ")}
              </h3>
              <p className="t-body showcase__card-sub">{s.sub}</p>
            </div>
          </article>
        )}
      />
    </div>
  );
}
